import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useFormik } from "formik";
import Drawer from "../../../../../components/SideDrawer/SideDrawer";
import { CreateViewForm } from "../../HelpDesk/components/CreateView/components/CreateViewForm";
import { DealsCreateViewFilters } from "./DealsCreateViewFilters";
import {
  type ViewFormValues,
  type FilterGroupUI,
  INITIAL_VIEW_FORM_VALUES,
  viewFormValidationSchema,
  buildDealsFilterGroupsPayload,
  normalizeDealsFilterGroups,
} from "../utils/dealsViewConstants";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import {
  useCreateDealsViewMutation,
  useUpdateDealsViewMutation,
} from "../../../../../redux/services/dealsApi";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../redux/store";
import {
  setNewlyCreatedDealsViewId,
  addDealsView,
  updateDealsViewInState,
} from "../../../../../redux/slices/dealsSlice";
import { clearDrawer } from "../../../../../redux/slices/drawerSlice";

export const DealsCreateEditView = () => {
  const dispatch = useDispatch();
  const drawerState = useSelector((state: RootState) => state.drawer);
  const dealsViews = useSelector((state: RootState) => state.deals.dealsViews);

  const isDrawerOpen = Boolean(
    drawerState?.open &&
    (drawerState?.type === "deals_view" ||
      drawerState?.type === "deals_edit_view")
  );
  const isEditMode = drawerState?.type === "deals_edit_view";
  const editViewId = isEditMode ? drawerState?.id : undefined;

  const [step, setStep] = useState<"form" | "filters">("form");
  const [viewEmoji, setViewEmoji] = useState("");

  const [createDealsView] = useCreateDealsViewMutation();
  const [updateDealsViewApi] = useUpdateDealsViewMutation();

  const editViewData = useMemo(
    () => dealsViews.find((v) => v.id === editViewId),
    [dealsViews, editViewId]
  );

  const formik = useFormik<ViewFormValues>({
    initialValues: INITIAL_VIEW_FORM_VALUES,
    validationSchema: viewFormValidationSchema,
    onSubmit: async (values) => {
      const filterGroups = buildDealsFilterGroupsPayload(values?.filter_groups);

      const payload = {
        name: values?.name,
        scope: values?.scope as "private" | "public",
        filter_groups: filterGroups,
        logical_operator: values?.logical_operator,
        emoji_icon: viewEmoji,
      };

      try {
        if (editViewId) {
          const result = await updateDealsViewApi({
            viewId: editViewId,
            ...payload,
          }).unwrap();
          dispatch(
            updateDealsViewInState({
              id: editViewId,
              ...payload,
              updated_at: result?.updated_at,
            })
          );
          customToast("View updated successfully", "success");
        } else {
          const result = await createDealsView(payload).unwrap();
          const newView = {
            id: result?.id ?? `deals_view_${Date.now()}`,
            ...payload,
            created_at: result?.created_at,
            updated_at: result?.updated_at,
          };
          dispatch(addDealsView(newView));
          dispatch(setNewlyCreatedDealsViewId(newView.id));
          customToast("View created successfully", "success");
        }
        handleClose();
      } catch (error: unknown) {
        const errorMessage =
          (error as { data: { detail: string } })?.data?.detail ||
          "Failed to save view";
        customToast(errorMessage, "error");
      }
    },
  });

  const handleClose = () => {
    setStep("form");
    setViewEmoji("");
    formik.resetForm({ values: INITIAL_VIEW_FORM_VALUES });
    dispatch(clearDrawer());
  };

  const handleNext = async () => {
    formik.setFieldTouched("name", true);
    formik.setFieldTouched("scope", true);
    const errors = await formik.validateForm();
    if (errors?.name || errors?.scope) return;
    setStep("filters");
  };

  useEffect(() => {
    if (!isDrawerOpen || drawerState?.type !== "deals_view") return;
    setStep("form");
    setViewEmoji("");
    formik.resetForm({ values: INITIAL_VIEW_FORM_VALUES });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, drawerState?.type]);

  useEffect(() => {
    if (!isEditMode || !editViewData) return;
    formik.resetForm({
      values: {
        name: editViewData.name || "",
        scope: (editViewData.scope as "private" | "public") || "",
        filter_groups: normalizeDealsFilterGroups(
          editViewData.filter_groups as FilterGroupUI[]
        ),
        logical_operator:
          (editViewData.logical_operator as "and" | "or") || "and",
      },
    });
    setViewEmoji(editViewData.emoji_icon || "");
    setStep("filters");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editViewData]);

  const hasUnsavedChanges = useMemo(() => {
    const currentValues = formik.values;
    const initialValues = formik.initialValues;
    return (
      !_.isEqual(currentValues.filter_groups, initialValues.filter_groups) ||
      currentValues.logical_operator !== initialValues.logical_operator
    );
  }, [formik.values, formik.initialValues]);

  const scopeDisplay =
    formik.values.scope === "public"
      ? "everyone"
      : formik.values.scope === "private"
        ? "private"
        : "";

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={handleClose}
      direction="right"
      size="550px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
      {step === "form" ? (
        <CreateViewForm
          formTitle={isEditMode ? "Edit view" : "Create view"}
          viewName={formik.values.name}
          viewEmoji={viewEmoji}
          sharedWith={scopeDisplay}
          onViewNameChange={(name) => formik.setFieldValue("name", name)}
          onEmojiChange={setViewEmoji}
          onSharedWithChange={(value) =>
            formik.setFieldValue(
              "scope",
              value === "everyone" ? "public" : "private"
            )
          }
          scopeError={
            formik?.touched?.scope && formik?.errors?.scope
              ? formik?.errors?.scope
              : ""
          }
          onClose={handleClose}
          onNext={handleNext}
          isEditMode={isEditMode}
        />
      ) : (
        <DealsCreateViewFilters
          filterGroups={formik.values.filter_groups}
          logicalOperator={formik.values.logical_operator}
          onFilterGroupsChange={(groups) =>
            formik.setFieldValue("filter_groups", groups)
          }
          onLogicalOperatorChange={(operator) =>
            formik.setFieldValue("logical_operator", operator)
          }
          onBack={() => setStep("form")}
          onClose={handleClose}
          onSave={() => formik.handleSubmit()}
          isEditMode={isEditMode}
          isSaveViewLoading={formik.isSubmitting}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      )}
    </Drawer>
  );
};
